import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import api from '../api'

export default function Login() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const resolveDestination = async (): Promise<string> => {
    try {
      const res = await api.get('/profile')
      const profile = res.data?.profile
      const hasProfile = profile && Object.keys(profile).length > 0 && profile.work_pattern
      return hasProfile ? '/dashboard' : '/onboarding'
    } catch (err: any) {
      // 404 or empty profile means onboarding not done yet
      if (err?.response?.status === 404) return '/onboarding'
      // Any other failure: do not strand the user, send them to the dashboard
      return '/dashboard'
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return

    setError('')
    setLoading(true)

    try {
      await login(email, password)
      const destination = await resolveDestination()
      navigate(destination, { replace: true })
    } catch (err: any) {
      const status = err?.response?.status
      const data = err?.response?.data

      if (status === 429) {
        setError('Too many login attempts. Please wait 15 minutes before trying again.')
      } else if (status === 403 && data?.code === 'EMAIL_NOT_VERIFIED') {
        setError('Please verify your email before logging in. Check your inbox for the verification link.')
      } else {
        setError(data?.error || 'Invalid email or password')
      }
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px 14px', fontSize: '14px',
    border: '1.5px solid var(--warm-border)',
    borderRadius: 'var(--radius-md)', outline: 'none',
    fontFamily: 'var(--font-body)', color: 'var(--ink)',
    background: 'var(--cream)', boxSizing: 'border-box',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '13px', fontWeight: 600,
    color: 'var(--ink)', marginBottom: '8px',
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--cream)',
      display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>

        <div
          onClick={() => navigate('/')}
          style={{
            fontFamily: 'var(--font-display)', fontSize: '28px',
            fontWeight: 700, color: 'var(--navy)',
            textAlign: 'center', marginBottom: '40px', cursor: 'pointer',
          }}
        >
          Velu<span style={{ color: 'var(--gold)' }}>mio</span>
        </div>

        <div style={{
          background: 'var(--white)', borderRadius: 'var(--radius-lg)',
          padding: '40px', boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--cream-dark)',
        }}>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: '24px',
            marginBottom: '8px', color: 'var(--navy)',
          }}>
            Welcome back
          </h2>
          <p style={{
            fontSize: '14px', color: 'var(--muted)', marginBottom: '32px',
          }}>
            Sign in to your Velumio account
          </p>

          <form onSubmit={handleSubmit}>

            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Email address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                style={inputStyle}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--gold)' }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--warm-border)' }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Your password"
                required
                autoComplete="current-password"
                style={inputStyle}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--gold)' }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--warm-border)' }}
              />
            </div>

            <div style={{ textAlign: 'right', marginBottom: '24px' }}>
              <Link
                to="/forgot-password"
                style={{ fontSize: '13px', color: 'var(--gold)', textDecoration: 'none' }}
              >
                Forgot password?
              </Link>
            </div>

            {error && (
              <div style={{
                padding: '12px 14px', background: 'var(--rose-light)',
                borderRadius: 'var(--radius-md)', fontSize: '13px',
                color: 'var(--rose)', marginBottom: '20px', lineHeight: 1.5,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password}
              style={{
                width: '100%', padding: '14px', fontSize: '16px',
                fontWeight: 600, color: 'var(--white)',
                background: loading || !email || !password
                  ? 'var(--muted)' : 'var(--navy)',
                border: 'none', borderRadius: 'var(--radius-md)',
                cursor: loading || !email || !password ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-body)', marginTop: '4px',
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>

          </form>
        </div>

        <p style={{
          textAlign: 'center', marginTop: '24px',
          fontSize: '14px', color: 'var(--muted)',
        }}>
          Don't have an account?{' '}
          <Link to="/register" style={{ color: 'var(--gold)', fontWeight: 500 }}>
            Create one free
          </Link>
        </p>

      </div>
    </div>
  )
}